import {
  type FirebaseOptions,
  initializeApp,
  provideFirebaseApp,
} from '@angular/fire/app';
import { Auth } from '@angular/fire/auth';
import { getFirestore, provideFirestore } from '@angular/fire/firestore';
import { Storage } from '@angular/fire/storage';
import { applicationConfig, type Decorator } from '@storybook/angular';
import { type User } from 'firebase/auth';
import { of } from 'rxjs';

const user: Partial<User> = {
  emailVerified: true,
  displayName: 'derpa man',
  email: 'derpa.man@derpa.stan',
};

const STORYBOOK_FIREBASE_CONFIG: FirebaseOptions = {
  projectId: 'storybook-mock-project',
  apiKey: 'fake-api-key',
  authDomain: 'storybook-mock-project.firebaseapp.com',
  storageBucket: 'storybook-mock-project.appspot.com',
  messagingSenderId: '000000000000',
  appId: '1:000000000000:web:0000000000000000000000',
};

/**
 * Storybook decorator to provide AngularFire with mock Firebase configuration
 * @deprecated Use `decorateStorybookCommon` instead
 */
export function decorateAngularFire(
  override: Partial<FirebaseOptions> = {}
): Decorator {
  const config: FirebaseOptions = {
    ...STORYBOOK_FIREBASE_CONFIG,
    ...override,
  };
  return applicationConfig({
    providers: [
      provideFirebaseApp(() => initializeApp(config)),
      provideFirestore(() => getFirestore()),
      {
        provide: Auth,
        useValue: {
          onIdTokenChanged: () => of(user),
          user: of(user),
          idTokenResult: of({
            claims: {
              isManagement: true,
            },
          }),
          authState: of({
            photoURL:
              'https://www.gravatar.com/avatar/d4c74594d841139328695756648b6bd6?d=identicon&r=PG',
          }),
          // eslint-disable-next-line no-console
          signOut: () => console.log('signOut'),
        },
      },
      {
        provide: Storage,
        // This mock satisfies both the compat API (.ref()) and the functional
        // API used by @angular/fire/storage's zone-wrapped ref() function.
        //
        // The functional ref(storage, path) calls _getChild$1(storage, path)
        // which reads storage._location.path and storage._location.bucket.
        // It then creates a Reference with storage.storage as the backing
        // service. getMetadata(ref) calls ref.storage.makeRequestWithTokens()
        // which we mock to return valid metadata, preventing network calls.
        useValue: (() => {
          const mockStorageService = {
            makeRequestWithTokens: () =>
              Promise.resolve({
                name: 'mock-file.jpg',
                bucket: 'storybook-mock-project.appspot.com',
                fullPath: 'mock/path/mock-file.jpg',
                contentType: undefined as string | undefined,
                size: 1024,
                timeCreated: new Date().toISOString(),
                updated: new Date().toISOString(),
                generation: '1',
                metageneration: '1',
                md5Hash: 'mock-hash',
                customMetadata: {},
              }),
            _getAuthToken: () => Promise.resolve(undefined),
            _getAppCheckToken: () => Promise.resolve(undefined),
            host: 'firebasestorage.googleapis.com',
            _protocol: 'https',
            _bucket: {
              path: '',
              bucket: 'storybook-mock-project.appspot.com',
            },
            _location: {
              path: '',
              bucket: 'storybook-mock-project.appspot.com',
            },
          };
          return {
            // Functional API: _location is read by _getChild$1(storage, path)
            _location: {
              path: '',
              bucket: 'storybook-mock-project.appspot.com',
            },
            // _getChild$1 creates new Reference(storage.storage, location)
            // so storage.storage must have makeRequestWithTokens
            storage: mockStorageService,
            // Compat API: used by older code that calls storage.ref(path)
            ref: (src: string) => ({
              getDownloadURL: () => of(src),
            }),
          };
        })(),
      },
    ],
  });
}
